import {StyleSheet} from '@react-pdf/renderer';
import type {QuoteFont} from '../resolver/types';

// The document ink and its white-blended tints (react-pdf-safe hex instead of rgba()).
// Exported as one palette so the HTML live preview renders from the same colors.
export const QUOTE_DOC_COLORS = {
	ink: '#1d2046',
	ink86: '#3d3f60',
	ink78: '#4f516f',
	ink70: '#61637d',
	ink60: '#777990',
	ink55: '#838499',
	ink50: '#8e90a2',
	ink45: '#999bac',
	ink42: '#a0a1b1',
	ink40: '#a5a6b5',
	borderStrong: '#d6d7de',
	borderGrand: '#dbdbe1',
	borderMeta: '#e4e4e9',
	borderMid: '#e8e9ec',
	borderSoft: '#ededf0',
	borderFaint: '#efeff2',
} as const;
export const INK = QUOTE_DOC_COLORS.ink;
const INK_86 = QUOTE_DOC_COLORS.ink86;
const INK_78 = QUOTE_DOC_COLORS.ink78;
const INK_70 = QUOTE_DOC_COLORS.ink70;
const INK_60 = QUOTE_DOC_COLORS.ink60;
const INK_55 = QUOTE_DOC_COLORS.ink55;
const INK_50 = QUOTE_DOC_COLORS.ink50;
const INK_45 = QUOTE_DOC_COLORS.ink45;
const INK_42 = QUOTE_DOC_COLORS.ink42;
const INK_40 = QUOTE_DOC_COLORS.ink40;
const BORDER_STRONG = QUOTE_DOC_COLORS.borderStrong;
const BORDER_GRAND = QUOTE_DOC_COLORS.borderGrand;
const BORDER_META = QUOTE_DOC_COLORS.borderMeta;
const BORDER_MID = QUOTE_DOC_COLORS.borderMid;
const BORDER_SOFT = QUOTE_DOC_COLORS.borderSoft;
const BORDER_FAINT = QUOTE_DOC_COLORS.borderFaint;

// Resolve the regular / bold PostScript font names for the supported quote fonts.
const fontPair = (font?: QuoteFont) => {
	if (font === 'times') return {regular: 'Times-Roman', bold: 'Times-Bold'};
	if (font === 'courier') return {regular: 'Courier', bold: 'Courier-Bold'};
	return {regular: 'Helvetica', bold: 'Helvetica-Bold'};
};

// Style factory only — every consumer builds its styles from the document's font, so there is
// no static default export to drift out of sync with the factory.
export const buildStyles = (font?: QuoteFont) => {
	const {regular, bold} = fontPair(font);
	return StyleSheet.create({
		bold: {fontFamily: bold},
		// ---- Page chrome -------------------------------------------------------
		page: {
			paddingTop: 84,
			paddingBottom: 72,
			paddingHorizontal: 48,
			fontFamily: regular,
			fontSize: 10,
			color: INK,
		},
		band: {
			position: 'absolute',
			top: 28,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			paddingBottom: 10,
			borderBottom: `1px solid ${BORDER_MID}`,
		},
		bandCover: {
			position: 'absolute',
			top: 28,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'flex-end',
			alignItems: 'center',
			paddingBottom: 10,
		},
		bandRun: {flexDirection: 'row', alignItems: 'center'},
		bandLogoImage: {height: 14, width: 42, objectFit: 'contain', objectPosition: 'left', marginRight: 8},
		bandLogoWord: {fontSize: 9, fontFamily: bold, color: INK_70, marginRight: 8},
		bandRunText: {fontSize: 8, color: INK_55},
		bandConfidential: {fontSize: 7, letterSpacing: 1.1, textTransform: 'uppercase', color: INK_40},
		content: {flex: 1},
		foot: {
			position: 'absolute',
			bottom: 26,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'space-between',
			borderTop: `1px solid ${BORDER_SOFT}`,
			paddingTop: 8,
		},
		footText: {fontSize: 8, color: INK_45},
		// ---- Shared section pieces ----------------------------------------------
		section: {marginBottom: 22},
		sectionLabelRow: {flexDirection: 'row', alignItems: 'center', marginBottom: 13},
		sectionLabelBar: {width: 16, height: 2, borderRadius: 1, marginRight: 8},
		sectionLabelText: {fontSize: 9, fontFamily: bold, letterSpacing: 1.3, textTransform: 'uppercase'},
		paragraph: {fontSize: 10, lineHeight: 1.65, marginBottom: 9, color: INK_86},
		// ---- Cover ----------------------------------------------------------------
		coverContent: {flex: 1, justifyContent: 'space-between', paddingVertical: 6},
		coverLogoImage: {height: 40, width: 150, objectFit: 'contain', objectPosition: 'left'},
		coverLogoWord: {fontSize: 15, fontFamily: bold},
		coverEyebrow: {fontSize: 9, fontFamily: bold, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 13},
		coverTitle: {fontSize: 32, fontFamily: bold, lineHeight: 1.1, marginBottom: 10},
		coverFor: {fontSize: 16, color: INK_60, marginBottom: 19},
		coverPrepared: {fontSize: 10, color: INK_70},
		coverMeta: {flexDirection: 'row', flexWrap: 'wrap', borderTop: `1px solid ${BORDER_META}`, paddingTop: 19},
		coverMetaCell: {width: '50%', marginBottom: 14},
		coverMetaLabel: {fontSize: 8, letterSpacing: 0.8, textTransform: 'uppercase', color: INK_45, marginBottom: 4},
		coverMetaValue: {fontSize: 12, fontFamily: bold},
		// ---- Estimate table -------------------------------------------------------
		tableHeadRow: {flexDirection: 'row', borderBottom: `1.5px solid ${BORDER_STRONG}`, paddingBottom: 7},
		th: {fontSize: 8, fontFamily: bold, letterSpacing: 0.8, textTransform: 'uppercase', color: INK_50},
		thNum: {textAlign: 'right'},
		colLabel: {flex: 1, paddingRight: 8},
		colNum: {width: 74},
		phaseRow: {
			flexDirection: 'row',
			alignItems: 'flex-end',
			paddingTop: 12,
			paddingBottom: 6,
			borderBottom: `1px solid ${BORDER_MID}`,
		},
		phaseCell: {flexDirection: 'row', alignItems: 'flex-end'},
		phaseIndex: {fontSize: 7.5, fontFamily: bold, letterSpacing: 0.8, textTransform: 'uppercase', marginRight: 8},
		phaseName: {fontSize: 11, fontFamily: bold},
		phaseSubtotal: {fontSize: 10.5, fontFamily: bold, textAlign: 'right'},
		deliverableRow: {flexDirection: 'row', alignItems: 'center', paddingTop: 11, paddingBottom: 2, paddingLeft: 10},
		deliverableLabel: {fontSize: 7, fontFamily: bold, letterSpacing: 0.9, textTransform: 'uppercase', marginRight: 8},
		deliverableName: {fontSize: 8.5, fontFamily: bold},
		itemRow: {flexDirection: 'row', paddingTop: 8, paddingBottom: 8, borderBottom: `1px solid ${BORDER_FAINT}`},
		itemLabelCell: {flexDirection: 'row', alignItems: 'center'},
		itemName: {fontSize: 10},
		itemNum: {fontSize: 10, textAlign: 'right'},
		itemTotal: {fontSize: 10, fontFamily: bold, textAlign: 'right'},
		expenseTag: {
			fontSize: 7,
			fontFamily: bold,
			letterSpacing: 0.9,
			textTransform: 'uppercase',
			color: INK_42,
			marginRight: 7,
		},
		expensesTotalRow: {flexDirection: 'row', paddingTop: 8, paddingBottom: 8, borderBottom: `1px solid ${BORDER_FAINT}`},
		expensesTotalLabel: {fontSize: 10, fontFamily: bold},
		rollupRow: {flexDirection: 'row', paddingTop: 10, paddingBottom: 10, borderBottom: `1px solid ${BORDER_FAINT}`},
		rollupName: {fontSize: 10.5, fontFamily: bold},
		// ---- Estimate totals ------------------------------------------------------
		totalsWrap: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: 18},
		totalsGrid: {width: 250},
		totalsRow: {flexDirection: 'row', justifyContent: 'space-between', marginBottom: 7},
		totalsLabel: {fontSize: 10, color: INK_70},
		totalsValue: {fontSize: 10, textAlign: 'right'},
		totalsGrandRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			borderTop: `1.5px solid ${BORDER_GRAND}`,
			paddingTop: 9,
			marginTop: 3,
		},
		totalsGrandLabel: {fontSize: 12, fontFamily: bold},
		totalsGrandValue: {fontSize: 12, fontFamily: bold, textAlign: 'right'},
		// ---- Terms ----------------------------------------------------------------
		termRow: {flexDirection: 'row', marginBottom: 13},
		termNumber: {width: 16, fontSize: 11, fontFamily: bold},
		termBody: {flex: 1},
		termHeading: {fontSize: 10, fontFamily: bold, marginBottom: 3},
		termText: {fontSize: 9.5, lineHeight: 1.6, color: INK_78},
		// ---- Agreement / signatures ----------------------------------------------
		signGrid: {flexDirection: 'row', marginTop: 28},
		signParty: {flex: 1},
		signPartyFirst: {flex: 1, marginRight: 36},
		signOrg: {fontSize: 11, fontFamily: bold, marginBottom: 18},
		signField: {marginTop: 22},
		signSignatureSpace: {height: 22},
		signLine: {borderBottom: `1px solid ${INK_40}`, marginBottom: 5},
		signLabel: {fontSize: 8, letterSpacing: 0.6, textTransform: 'uppercase', color: INK_50},
		signNote: {marginTop: 28, fontSize: 8.5, lineHeight: 1.5, color: INK_50},
	});
};
